import React, { useRef, useState } from 'react'

import MainPageLayout from '../../../ui/MainPage.layout'
import { WidgetLayout } from '../../../lib/widget-layout'
import useText from '../../../common/hooks/useText'
import { Button } from '../../../common/components/Button'
import { ExternalServerCreateModal } from '../../components/external-server-create-modal'
import Text from '../../../ui/Text'
import { useExternalServerGetAllQuery, ExternalServer } from '../../../common/redux/api/web-api'
import { ChargersPagination } from '../../../lib/chargers-pagination'
import ButtonGroupPagination from '../../../ui/common/ButtonGroupPagination'
import { filterFieldsBySearchValue } from '../../../lib/filter-fields-by-search-value'
import { Table, TableBody, TableHeader, TableHeaderCell, TableRow, TableBodyCell } from '../../../lib/table'
import { STableLink } from '../../../lib/table/Table.styles'
import { DeleteExternalServerModal } from '../../components/delete-external-server-modal'
import { InfoIcon } from '../../../lib/icons/info-icon'
import Tooltip from '../../../common/components/Tooltip'
import { theme } from '../../../lib/theme'

export const ExternalServersPage: React.FC = () => {
    const text = useText()
    const [createModal, setCreateModal] = useState(false)
    const [deleteModal, setDeleteModal] = useState(false)
    const [externalServer, setExternalServer] = useState<ExternalServer>()
    const [currentPage, setCurrentPage] = useState(0)
    const [searchValue, setSearchValue] = useState('')

    const [pageSize, setPageSize] = useState(20)
    const offset = currentPage * pageSize

    const filtersContainerRef = useRef<HTMLDivElement | null>(null)
    const handlePageClick = (selected: number) => {
        window.scrollTo(0, filtersContainerRef.current?.offsetTop || 0)
        setCurrentPage(selected)
    }

    const closeModal = () => {
        setCreateModal(false)
        setExternalServer(undefined)
    }

    const closeDeleteModal = () => {
        setDeleteModal(false)
        setExternalServer(undefined)
    }

    const { data: externalServerData, isLoading: dataLoading } = useExternalServerGetAllQuery()

    return (
        <MainPageLayout data-testid="external-server-page">
            <div className="row margin-top-small">
                <div className="col-md-12 col-xs-12">
                    <WidgetLayout
                        data-testid="external-server-widget"
                        title={text('lblProxyRelayWidget')}
                        headerInfoIcon={
                            <span className="margin-top-6">
                                <Tooltip content={'lblProxyRelayInfoText'} className="large-content">
                                    <InfoIcon size={18} fill={theme.brandColors.CA_Blue} id="eapi-info-icon" />
                                </Tooltip>
                            </span>
                        }
                        loadingStatus={dataLoading}
                        headerButtons={[
                            <Button
                                label={text('btnCreate')}
                                size="sm"
                                onClick={() => setCreateModal(true)}
                                data-testid="create-button"
                            ></Button>,
                        ]}
                    >
                        <div>
                            <>
                                <div className="col-xs-3 alpha margin-bottom-small">
                                    <input
                                        data-testid="search"
                                        type="text"
                                        name="search"
                                        maxLength={1000}
                                        id="search"
                                        value={searchValue}
                                        onChange={(event) => setSearchValue(event.target.value)}
                                        className="form-control"
                                        placeholder={text('lblSearchField')}
                                    />
                                </div>
                                {externalServerData && externalServerData?.length > 20 && (
                                    <div className="text-right">
                                        <div data-testid="pagination-title">
                                            <Text code="msgResultPage" />
                                            <span>:</span>
                                        </div>
                                        <ButtonGroupPagination
                                            value={pageSize}
                                            onChange={(value: number) => {
                                                setPageSize(value), setCurrentPage(0)
                                            }}
                                            data-testid="button-paginate-group"
                                        ></ButtonGroupPagination>
                                    </div>
                                )}
                            </>
                        </div>

                        <div className="table-container" data-testid="server-list-table">
                            <Table>
                                <TableHeader>
                                    <TableRow clickable={false}>
                                        <TableHeaderCell data-testid="name-heading">
                                            <Text code="lblCpmsName" />
                                        </TableHeaderCell>
                                        <TableHeaderCell data-testid="url-heading">
                                            <Text code="lblCpmsUrl" />
                                        </TableHeaderCell>

                                        <TableHeaderCell data-testid="remove-heading">
                                            <Text code="lblRemove" />
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody dataTestId="server-list">
                                    {externalServerData &&
                                        externalServerData
                                            ?.filter((server) =>
                                                filterFieldsBySearchValue(
                                                    [server?.description ?? undefined, server?.url ?? undefined],
                                                    searchValue
                                                )
                                            )
                                            .slice(offset, offset + pageSize)
                                            .map((server) => (
                                                <TableRow key={server.id} clickable={false}>
                                                    <TableBodyCell data-testid={`name-${server?.description}`}>
                                                        <STableLink
                                                            onClick={() => {
                                                                if (!deleteModal) {
                                                                    setCreateModal(true)
                                                                }
                                                                setExternalServer(server)
                                                            }}
                                                        >
                                                            {server?.description}
                                                        </STableLink>
                                                    </TableBodyCell>
                                                    <TableBodyCell data-testid={`name-${server?.url}`}>
                                                        {server?.url}
                                                    </TableBodyCell>

                                                    <TableBodyCell>
                                                        <button
                                                            data-testid="remove-button"
                                                            type="button"
                                                            className="btn btn-danger btn-xs btn-icon margin-left-small"
                                                            onClick={() => {
                                                                setDeleteModal(true)
                                                                setExternalServer(server)
                                                            }}
                                                        >
                                                            <span className="fa fa-remove"></span>
                                                        </button>
                                                    </TableBodyCell>
                                                </TableRow>
                                            ))}
                                </TableBody>
                            </Table>
                        </div>

                        {externalServerData && externalServerData?.length > 0 && (
                            <ChargersPagination
                                data-testid="pagination"
                                setPageIndex={handlePageClick}
                                totalCount={externalServerData?.length}
                                pageSize={pageSize}
                                pageIndex={currentPage}
                                displayedChargers={pageSize}
                            />
                        )}
                    </WidgetLayout>
                </div>
            </div>
            {createModal && (
                <ExternalServerCreateModal
                    closeModal={closeModal}
                    externalServerData={externalServerData}
                    externalServer={externalServer}
                />
            )}

            {deleteModal && <DeleteExternalServerModal closeModal={closeDeleteModal} externalServer={externalServer} />}
        </MainPageLayout>
    )
}
